import React from 'react';
import { resolveBillingContract, BILLING_AUTHORITY } from '@hopdrive/sdk-ar';

export interface BillingAuthorityState {
  /** A program coverage contract prices this customer's moves. */
  programGoverns: boolean;
  /**
   * The rooftop holds more than one Valet product and nothing says which one
   * this move is bought under, so no contract can be named. Resolving it as
   * Full Service would show a dealer on both tiers Full Service coverage for a
   * move that might bill as Lite. Distinct from `programGoverns: false`, which
   * is a real answer.
   */
  productAmbiguous: boolean;
  /** Filled only when `productAmbiguous`, so the UI can name the candidates. */
  candidateProductKeys: string[];
  loading: boolean;
}

const IDLE: BillingAuthorityState = {
  programGoverns: false,
  productAmbiguous: false,
  candidateProductKeys: [],
  loading: false,
};

/**
 * Asks sdk-ar which contract prices the selected customer's moves, so the
 * planner stops showing the customer's own rate for a move a program coverage
 * contract will price instead. Not enrolled, or enrolled in a program that only
 * adds a per-move fee, both resolve to the customer's own contract.
 *
 * @param asOf ISO timestamp the move will bill on. Program enrollment and
 *   contracts are date-windowed, so the planned pickup is the honest input.
 *   The planner's `plannerBillingDate` decides the fallback to "now".
 * @param productKey The Valet product the move is bought under, when known.
 *   It is the identity `scenarios.product_key` will carry, so the estimate and
 *   the eventual AR agree by construction. Without it, a rooftop on both tiers
 *   comes back `productAmbiguous` and the coverage bar asks instead of guessing.
 */
export function useBillingAuthority(
  customerId?: number | null,
  lite?: boolean | null,
  asOf?: string,
  productKey?: string | null,
): BillingAuthorityState {
  const [state, setState] = React.useState<BillingAuthorityState>(IDLE);

  React.useEffect(() => {
    if (typeof customerId !== 'number' || customerId <= 0) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    setState({ ...IDLE, loading: true });
    resolveBillingContract({ customerId, lite, asOf, productKey })
      .then(result => {
        if (cancelled) return;
        setState({
          programGoverns: result.authority === BILLING_AUTHORITY.PROGRAM,
          productAmbiguous: result.authority === BILLING_AUTHORITY.UNRESOLVED,
          candidateProductKeys:
            result.authority === BILLING_AUTHORITY.UNRESOLVED ? result.candidateProductKeys : [],
          loading: false,
        });
      })
      .catch(err => {
        // A failed read is not an ambiguity. IDLE keeps the planner on the
        // customer's own rate.
        console.error('useBillingAuthority: resolveBillingContract failed', err);
        if (!cancelled) setState(IDLE);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, lite, asOf, productKey]);

  return state;
}
